export const concertProductionFallback = {
  page: {
    path: '/concert-production',
    canonicalUrl: 'https://miamisoundrental.com/concert-production'
  },
  revision: {
    seo: {
      title: 'Concert Production Company in Miami & South Florida | MSR',
      description: 'Plan concerts, festivals, and live shows with one South Florida production team for audio, lighting, video, staging, technical crew, and show operation.',
      canonicalUrl: 'https://miamisoundrental.com/concert-production',
      robots: {
        index: true,
        follow: true
      },
      primaryKeyword: 'concert production company Miami',
      secondaryKeywords: [
        'concert production Miami',
        'concert AV production',
        'live concert production',
        'festival production Miami',
        'live show production South Florida'
      ],
      searchIntent: 'commercial',
      targetLocations: ['Miami', 'Fort Lauderdale', 'South Florida'],
      openGraph: {
        type: 'website',
        url: 'https://miamisoundrental.com/concert-production',
        title: 'Concert Production Company in Miami & South Florida | MSR',
        description: 'One South Florida production team for concert audio, lighting, LED video, staging, technical crew, advancing, and live show operation.',
        images: [{
          url: 'https://dnw-server.s3.us-east-2.amazonaws.com/myImage-1684264150830.jpg',
          width: 1600,
          height: 1200,
          alt: 'Concert audio, lighting, LED video, and staging produced by Miami Sound Rental'
        }]
      },
      twitter: {
        cardType: 'summary_large_image'
      }
    },
    content: {
      hero: {
        eyebrow: 'Concert production · Miami, Fort Lauderdale & South Florida',
        title: 'Concert & Live Show Production in South Florida',
        description: 'A South Florida concert production company for concert AV, live event production, lighting, LED video, staging, technical crew, advancing, soundcheck, and show operation.',
        image: '/Pictures/concert-landing.jpg?v=20260818',
        imageAlt: 'Outdoor live-event stage with professional concert lighting and sound production',
        primaryAction: 'Request a Production Proposal',
        secondaryAction: 'Call a Producer'
      },
      proof: [
        { key: 'rating', value: '4.8 / 5', label: 'Current Google rating' },
        { key: 'reviews', value: '268', label: 'Google reviews' },
        { key: 'coverage', value: 'South Florida', label: 'Local production coverage' },
        { key: 'delivery', value: 'One team', label: 'Advance through show operation' }
      ],
      eventTypes: {
        eyebrow: 'Built around the performance',
        title: 'Concert AV and live event production for every show format',
        description: 'The artist, venue, audience, schedule, technical rider, and show format determine the production—not a preselected equipment package.',
        items: [
          { title: 'Concerts & touring artists', description: 'Local production support, rider fulfillment, artist inputs, monitoring, playback, and coordinated show operation.' },
          { title: 'Festival production & multi-act events', description: 'Shared systems, changeovers, stage schedules, technical teams, and consistent audience coverage across the program.' },
          { title: 'DJ & electronic shows', description: 'High-impact audio, DJ backline, bass management, LED visuals, lighting, effects, and performer monitoring.' },
          { title: 'Municipal & public performances', description: 'Outdoor coverage, staging, video, schedules, documentation, and technical support for public programs.' },
          { title: 'Live performance showcases', description: 'Sound, lighting, staging, playback, cues, and operator support shaped around the performance.' }
        ]
      },
      capabilities: {
        eyebrow: 'One accountable production partner',
        title: 'Every concert discipline coordinated as one production',
        description: 'MSR combines equipment, technical labor, venue coordination, and live show operation into one managed scope.',
        items: [
          { icon: 'audio', title: 'Concert sound production', description: 'Concert audio production including system design, FOH, monitors, microphones, inputs, playback, artist communication, coverage, and system tuning.' },
          { icon: 'lighting', title: 'Lighting & effects', description: 'Stage lighting, moving fixtures, atmospheric effects, programming, cues, and live operation.' },
          { icon: 'video', title: 'LED video & visuals', description: 'LED video walls, projection, playback, switching, IMAG-ready infrastructure, and show visuals.' },
          { icon: 'staging', title: 'Staging & structures', description: 'Stages, risers, truss, drape, equipment positions, cable paths, and coordinated production layouts.' },
          { icon: 'crew', title: 'Technical crew & show operation', description: 'Production management, technicians, operators, load-in, soundcheck, show calls, changeovers, and strike.' },
          { icon: 'advance', title: 'Technical advancing', description: 'Rider review, venue coordination, schedules, power, access, inputs, backline requirements, and contingency planning.' }
        ]
      },
      caseStudies: [
        {
          slug: 'cyberbass-rave',
          title: 'CyberBass Rave',
          type: 'Electronic concert · North Miami',
          description: 'A bass-intensive show in a skate park with concert audio, DJ backline, LED video, moving lights, and a tall truss structure.',
          facts: ['6-person crew', '16 line-array speakers', '18′ × 12′ LED video wall'],
          image: 'https://dnw-server.s3.us-east-2.amazonaws.com/myImage-1684264150830.jpg',
          imageAlt: 'CyberBass Rave concert production in a North Miami skate park'
        },
        {
          slug: 'stellar-4th-of-july-in-north-miami!',
          title: 'North Miami July 4 Celebration',
          type: 'Public concert & festival · North Miami',
          description: 'Outdoor audio and video support for a city celebration combining performances, public activities, and a large community audience.',
          facts: ['5-person crew', 'Outdoor event support', 'Audio, displays, and video'],
          image: 'https://dnw-server.s3.us-east-2.amazonaws.com/myImage-1720144135915.jpg',
          imageAlt: 'Outdoor July 4 concert and festival production in North Miami'
        },
        {
          slug: 'a-spotlight-on-pole-dance-extravaganza',
          title: 'Pole Dance Extravaganza',
          type: 'Live performance showcase · Miami',
          description: 'A performance-focused production at Toe Jam Backlot using sound, lighting, and staging to support a tightly scheduled live competition.',
          facts: ['8-person crew', 'Performance staging', 'Sound and show lighting'],
          image: 'https://dnw-server.s3.us-east-2.amazonaws.com/myImage-1734921054606.jpg',
          imageAlt: 'Sound, lighting, and staging for a live performance showcase in Miami'
        }
      ],
      process: {
        eyebrow: 'One team from rider through strike',
        title: 'A concert-production process built to reduce show-day risk',
        description: 'The production team stays connected to the artist, venue, equipment, labor, schedule, and operating details throughout the project.',
        image: '/Pictures/concert-led-panel-technician.jpg?v=20260818',
        imageAlt: 'Miami Sound Rental technician assembling LED video panels beside stage truss for a live production',
        steps: [
          { title: 'Discovery & technical advance', description: 'We review the show format, venue, audience, schedule, artist rider, inputs, playback, monitoring, and production priorities.' },
          { title: 'System design & planning', description: 'The team develops equipment, labor, layouts, power, schedules, cable paths, logistics, and contingency requirements.' },
          { title: 'Load-in, tuning & soundcheck', description: 'Systems are installed, tested, tuned, rehearsed, adjusted, and handed over to the show team.' },
          { title: 'Show operation & strike', description: 'Operators support the artists, cues, changeovers, and audience experience through the show and an organized strike.' }
        ]
      },
      logistics: {
        eyebrow: 'Venue & show logistics',
        title: 'Plan the details that can derail a live show before load-in',
        description: 'A successful concert depends on more than equipment. The technical advance should surface access, power, rigging, schedules, staffing, documentation, and weather constraints early.',
        image: '/Pictures/concert-outdoor-production-logistics.jpg?v=20260818',
        imageAlt: 'Outdoor concert sound system and production equipment staged for a live event in Miami',
        items: [
          'Artist rider, input list, backline, playback, and monitor review',
          'Venue access, loading dock, parking, elevator, and load-in scheduling',
          'Power, rigging, stage, FOH, monitor, and equipment-position planning',
          'Certificates of insurance and vendor documentation',
          'Soundcheck, rehearsal, changeover, performance, and strike schedules',
          'Outdoor weather, cover, wind, power, and contingency considerations'
        ]
      },
      faqs: [
        { question: 'What is included in a managed concert production?', answer: 'A managed scope can include technical advancing, system design, equipment, crew, venue coordination, load-in, system tuning, soundcheck, live operation, changeovers, and strike. The exact scope depends on the artist, venue, audience, schedule, rider, and services required.' },
        { question: 'Can Miami Sound Rental fulfill an artist technical rider?', answer: 'Yes. Share the rider as early as possible. The production team will review audio inputs, microphones, monitoring, playback, backline, lighting, video, staging, power, labor, and scheduling, then identify substitutions or open questions before show day.' },
        { question: 'Do you support both indoor and outdoor concerts?', answer: 'Yes. Indoor and outdoor productions are evaluated differently. Outdoor work requires additional review of power, weather, wind, equipment cover, staging, access, noise requirements, and contingency plans.' },
        { question: 'How early should we request a concert production proposal?', answer: 'Contacting us as soon as the date and venue are being considered provides the most planning flexibility. Festivals, touring artists, outdoor shows, complex video, staging, or multi-act programs benefit from a longer advance, but the team can also evaluate time-sensitive requests.' },
        { question: 'Can you support a festival or multiple performers?', answer: 'Yes. The plan can include shared systems, artist inputs, backline coordination, stage schedules, line checks, soundchecks, changeovers, technical teams, and multiple performance areas when the venue and schedule allow.' },
        { question: 'Can I see preliminary pricing before the production is finalized?', answer: 'Yes. The Event Builder and preliminary package starting points can help with early budgeting. A final concert proposal still requires technical review of the venue, rider, labor, schedule, power, staging, and show-operation needs.' },
        { question: 'Which areas do you serve?', answer: 'Miami Sound Rental supports concerts and live shows across Miami, Fort Lauderdale, and South Florida. Larger productions outside the immediate area can be reviewed based on the venue, schedule, and project requirements.' },
        { question: 'Do you book artists or promote concerts?', answer: 'This page covers technical concert production and related production support. Talent booking, ticketing, promotion, and artist management are not automatically included unless they are specifically discussed in the project scope.' }
      ],
      proposal: {
        eyebrow: 'Start the production conversation',
        title: 'Request a concert production proposal',
        description: 'Share the date, venue, audience, artist information, services, and technical rider you already have. A producer will clarify the scope and next step.'
      },
      builder: {
        eyebrow: 'Still in the budgeting stage?',
        title: 'Build a preliminary concert plan online',
        description: 'Choose dates, a location, and preliminary equipment to explore pricing. The result is a planning starting point—not a substitute for technical review, advancing, crew, venue logistics, or show operation.',
        action: 'Open the Event Builder'
      },
      related: {
        eyebrow: 'Related production and rental paths',
        title: 'Continue with the service that matches the project',
        items: [
          { title: 'Corporate AV Production', description: 'Conferences, conventions, corporate meetings, presentations, and managed business events.', href: '/corporate-event-production' },
          { title: 'Sound System Rental', description: 'Equipment-focused PA and sound-system rental for events that do not require a complete concert production.', href: '/sound-system-rental' },
          { title: 'Line Array Rental', description: 'Specialized line-array systems for high-output coverage and larger audience areas.', href: '/sound/array' },
          { title: 'Production Portfolio', description: 'Review documented MSR productions, photography, crews, services, and technical scope.', href: '/portfolio' }
        ]
      }
    }
  }
}

export default concertProductionFallback
